import { useEffect, useRef, useState } from 'react'
import { Camera, X, Sun, Moon, Monitor, KeyRound } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { oidcAvailableInThisClient, oidcStartUrl } from '../lib/oidc'
import { useAuth } from '../store/auth'
import { useTheme, type ThemeChoice } from '../theme/theme'
import { Avatar, Button, useBackdropClose } from './ui'
import { AppFooter } from './AppFooter'
import { CalendarFeedSection } from './calendar/CalendarFeedSection'

const THEME_OPTS: ReadonlyArray<{ value: ThemeChoice; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Hell', icon: Sun },
  { value: 'dark', label: 'Dunkel', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
]

/**
 * Selbstbedienungs-Profilbild: jede angemeldete Person (auch Kinder) kann ihr
 * EIGENES Avatar setzen/entfernen. Der Server erlaubt Nicht-Erwachsenen nur den
 * eigenen (`me.id === id`, members.ts). Öffnet sich über den Avatar im Header.
 */
export function ProfileDialog({ onClose }: { onClose: () => void }): React.JSX.Element | null {
  const member = useAuth((s) => s.member)
  const [theme, setTheme] = useTheme()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const backdrop = useBackdropClose(onClose)
  // Authentik-Verknüpfung (ADR-082): null = Status noch unbekannt / SSO aus.
  const [sso, setSso] = useState<{ enabled: boolean; linked: boolean } | null>(null)

  useEffect(() => {
    if (!oidcAvailableInThisClient) return
    api.oidcStatus().then(setSso).catch(() => setSso(null))
  }, [])

  if (!member) return null

  async function disconnectSso(): Promise<void> {
    setBusy(true)
    try {
      await api.oidcDisconnect()
      setSso({ enabled: true, linked: false })
    } catch {
      setError('Verknüpfung konnte nicht gelöst werden.')
    } finally {
      setBusy(false)
    }
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !member) return
    setBusy(true)
    setError(null)
    try {
      const updated = await api.uploadAvatar(member.id, file)
      useAuth.setState({ member: updated })
    } catch (err) {
      const code = err instanceof ApiError ? err.message : ''
      setError(
        code === 'file_too_large'
          ? 'Bild ist zu groß (max. 15 MB). Bitte ein kleineres Foto wählen.'
          : code === 'no_image'
            ? 'Das ist keine gültige Bilddatei.'
            : 'Bild konnte nicht hochgeladen werden.',
      )
    } finally {
      setBusy(false)
    }
  }

  async function removeAvatar(): Promise<void> {
    if (!member) return
    setBusy(true)
    setError(null)
    try {
      const updated = await api.deleteAvatar(member.id)
      useAuth.setState({ member: updated })
    } catch {
      setError('Entfernen fehlgeschlagen. Bitte später erneut versuchen.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" {...backdrop}>
      <div
        className="max-h-[90vh] w-full max-w-sm overflow-auto rounded-t-2xl border p-5 sm:rounded-2xl"
        style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)', paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>Mein Profil</h2>
          <button onClick={onClose} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5"><X className="size-5" style={{ color: 'var(--color-muted)' }} /></button>
        </div>
        <div className="flex flex-col items-center gap-4">
          <Avatar name={member.name} color={member.color} size={96} src={member.avatarUrl} />
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} />
          <div className="flex flex-wrap justify-center gap-2">
            <Button onClick={() => fileRef.current?.click()} disabled={busy}>
              <Camera className="size-4" /> {member.avatarUrl ? 'Bild ändern' : 'Bild wählen'}
            </Button>
            {member.avatarUrl && (
              <Button variant="ghost" onClick={removeAvatar} disabled={busy}>Entfernen</Button>
            )}
          </div>
          {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
        </div>

        {/* Darstellung: Hell / Dunkel / System — geräte-lokale Wahl, für jede
            Person (auch Kinder) auf dem eigenen Gerät. (ADR-061) */}
        <div className="mt-6">
          <h3 className="mb-2 text-sm font-semibold" style={{ color: 'var(--color-ink)' }}>Darstellung</h3>
          <div className="grid grid-cols-3 gap-1 rounded-xl border p-1" role="group" aria-label="Darstellung" style={{ borderColor: 'var(--color-line)', backgroundColor: 'var(--color-surface)' }}>
            {THEME_OPTS.map((opt) => {
              const active = theme === opt.value
              const Icon = opt.icon
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTheme(opt.value)}
                  aria-pressed={active}
                  className="flex min-h-11 flex-col items-center justify-center gap-1 rounded-lg px-2 py-2 text-xs font-medium transition-colors"
                  style={{ backgroundColor: active ? 'var(--color-accent)' : 'transparent', color: active ? '#ffffff' : 'var(--color-muted)' }}
                >
                  <Icon className="size-5" />
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Authentik-Konto verbinden/trennen (ADR-082). Erscheint nur, wenn der
            Server SSO konfiguriert hat und wir im Web laufen. Das Wand-Tablet-Profil
            (guest) bleibt bewusst außen vor — es lebt davon, kein Geheimnis und
            keine persönliche Identität zu brauchen (ADR-075). Der Login per
            Passwort/PIN bleibt in jedem Fall bestehen. */}
        {sso?.enabled && member.role !== 'guest' && (
          <div className="mt-6">
            <h3 className="mb-2 text-sm font-semibold" style={{ color: 'var(--color-ink)' }}>Authentik-Konto</h3>
            <div className="rounded-xl border p-3" style={{ borderColor: 'var(--color-line)', backgroundColor: 'var(--color-surface)' }}>
              <p className="mb-3 text-sm" style={{ color: 'var(--color-muted)' }}>
                {sso.linked
                  ? 'Verbunden — du kannst dich zusätzlich mit deinem Authentik-Konto anmelden.'
                  : 'Nicht verbunden. Nach dem Verbinden kannst du dich wahlweise mit Authentik oder wie bisher mit Passwort/PIN anmelden.'}
              </p>
              {sso.linked ? (
                <Button variant="ghost" onClick={disconnectSso} disabled={busy}>Verknüpfung lösen</Button>
              ) : (
                <Button onClick={() => { window.location.href = oidcStartUrl('connect') }} disabled={busy}>
                  <KeyRound className="size-4" /> Authentik-Konto verbinden
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Kalender-Feed (ICS-Export, ADR-086): Erwachsene immer, Kinder nach
            Freischaltung durch Eltern, Wand-Tablet (guest) nie. */}
        {member.role !== 'guest' && <CalendarFeedSection />}

        {/* Kinder haben keinen Zugang zu den Einstellungen (Eltern-only) und sehen die
            App-Version sonst nirgends → Footer hier für Kinder anzeigen. */}
        {member.role === 'child' && <AppFooter />}
      </div>
    </div>
  )
}
